/**
 * Excel-like keyboard moves between the editable cells of a table (pure, so it is tested without
 * a DOM). A cell's place is its row and column index; only the cells that are editable right now
 * are passed in, so a read-only or hidden column is simply skipped.
 *
 * - Tab / Shift+Tab: the next / previous editable cell in reading order (row, then column),
 *   wrapping onto the next / previous row.
 * - Enter / ArrowDown: the same column in the nearest row below; Shift+Enter / ArrowUp: above.
 */
export interface GridPosition {
  row: number;
  column: number;
}

export type GridMove = "NEXT" | "PREVIOUS" | "DOWN" | "UP";

const TAB_KEY = "Tab";
const ENTER_KEY = "Enter";
const ARROW_DOWN_KEY = "ArrowDown";
const ARROW_UP_KEY = "ArrowUp";

/** The move a key asks for, or null when the key is the cell's own (typing, caret moves). */
export const gridMoveOfKey = (key: string, isShift: boolean): GridMove | null => {
  if (key === TAB_KEY) return isShift ? "PREVIOUS" : "NEXT";
  if (key === ENTER_KEY) return isShift ? "UP" : "DOWN";
  if (key === ARROW_DOWN_KEY) return "DOWN";
  if (key === ARROW_UP_KEY) return "UP";
  return null;
};

const compareReadingOrder = (left: GridPosition, right: GridPosition) =>
  left.row - right.row || left.column - right.column;

/** Where a move from `from` lands among `positions`, or null when there is no such cell. */
export const targetPositionOf = (
  positions: GridPosition[],
  from: GridPosition,
  move: GridMove,
): GridPosition | null => {
  const sorted = [...positions].sort(compareReadingOrder);
  if (move === "NEXT") {
    return sorted.find((position) => compareReadingOrder(position, from) > 0) ?? null;
  }
  if (move === "PREVIOUS") {
    return sorted.filter((position) => compareReadingOrder(position, from) < 0).pop() ?? null;
  }
  const sameColumn = sorted.filter((position) => position.column === from.column);
  if (move === "DOWN") return sameColumn.find((position) => position.row > from.row) ?? null;
  return sameColumn.filter((position) => position.row < from.row).pop() ?? null;
};

export const positionKeyOf = (position: GridPosition) => `${position.row}:${position.column}`;
